import "./ChatDialog.scss";
import React, { useRef, useState, useEffect } from "react";
import Avatar from "../Avatar";

export default function ChatDialog({
  isActive,
  isThinking,
  askQuestion,
  chatDialog,
}) {
  const inputRef = useRef(null);
  const chatRef = useRef(null);
  const listRef = useRef(null);
  const rootRef = useRef(null);
  const [chatInput, setChatInput] = useState("");

  const isOpen = isActive === true;

  const mountEl =
    typeof document !== "undefined"
      ? document.getElementById("sigil-chatbot")
      : null;
  const logoUrl = (mountEl && mountEl.dataset && mountEl.dataset.logoUrl) || "";
  const logoLargeUrl =
    (mountEl && mountEl.dataset && mountEl.dataset.logoLargeUrl) || "";
  const headerLogo = logoLargeUrl || logoUrl;

  const scrollToBottom = () => {
    if (chatRef.current) {
      chatRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  // Align the top of the most recent bot response with the top of the chat
  // panel, so the reader starts at the beginning of a long answer.
  const scrollToLatestResponse = () => {
    const container = listRef.current;
    if (!container) {
      return;
    }
    const bots = container.querySelectorAll(".from-chatbot");
    const el = bots[bots.length - 1];
    if (!el) {
      scrollToBottom();
      return;
    }
    const offset =
      el.getBoundingClientRect().top - container.getBoundingClientRect().top;
    container.scrollTo({
      top: container.scrollTop + offset - 8,
      behavior: "smooth",
    });
  };

  // Keep the closed panel out of the tab order / accessibility tree, and move
  // focus into the input only when the chat opens.
  useEffect(() => {
    if (rootRef.current) {
      rootRef.current.inert = !isOpen;
      rootRef.current.setAttribute("aria-hidden", isOpen ? "false" : "true");
    }
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  useEffect(() => {
    // Keep focus in the input during an active conversation only.
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
    const last = chatDialog[chatDialog.length - 1];
    if (!isThinking && last && last.from === "chatbot") {
      scrollToLatestResponse();
    } else {
      scrollToBottom();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatDialog, isThinking]);

  const handleChatSubmit = (e) => {
    e.preventDefault();
    setChatInput("");
    askQuestion(chatInput);
  };

  return (
    <div
      className={"ChatDialog" + (isOpen ? "" : " minimized")}
      ref={rootRef}
      role="dialog"
      aria-label="Commonwealth Chatbot"
    >
      <div className="chat-header">
        {headerLogo && <img src={headerLogo} alt="Commonwealth ChatBot" />}
        {/* <span>Commonwealth</span>*/}
      </div>

      <ul className="chat" ref={listRef}>
        {chatDialog.map((dialog, i) => {
          return (
            <DialogBubble
              key={i}
              from={dialog.from}
              text={dialog.text}
              citations={dialog.citations}
              links={dialog.links}
            />
          );
        })}

        {isThinking && (
          <DialogBubble key={"thinking"} from={"thinking"} text={null} />
        )}

        <li className="root-li" style={{ float: "left", clear: "both", listStyle: "none" }} ref={chatRef}></li>
      </ul>

      <div
        className={"chat-input is-disabled-" + (isThinking ? "true" : "false")}
      >
        <form
          className="chat-input-wrapper"
          action="#"
          onSubmit={handleChatSubmit}
        >
          <div className="chat-text-input">
            <label className="screen-reader-text" htmlFor="chat-input">
              Ask a question
            </label>
            <input
              type="text"
              id="chat-input"
              aria-label="Ask a question"
              placeholder="Ask a question…"
              disabled={isThinking}
              ref={inputRef}
              value={chatInput}
              onChange={(e) => setChatInput(e.target.value)}
            />
          </div>
          <button type="submit" aria-label="Send message">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 12L3.269 3.126A59.768 59.768 0 0121.485 12 59.77 59.77 0 013.27 20.876L5.999 12zm0 0h7.5"
              />
            </svg>
          </button>
        </form>
      </div>
    </div>
  );
}

function formatChatText(text) {
  if (typeof text !== "string") return text;

  let raw = text;
  const preMatch = raw.match(/^<pre>([\s\S]*)<\/pre>$/);
  if (preMatch) {
    raw = preMatch[1];
  }

  // The answer is untrusted: indexed pages and PDFs can carry text that talks
  // the model into emitting markup, and this all ends up in
  // dangerouslySetInnerHTML. Escape first, then add only our own markup —
  // strong, lists, and the link tokens, which are built with escaped values.
  raw = escapeHtml(raw);

  raw = raw.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");

  const lines = raw.split(/\r?\n/);
  const BULLET = /^[-*]\s+/;
  const NUMBERED = /^\d+[.)]\s+/;
  let html = "";
  let listTag = ""; // "" | "ul" | "ol"

  const closeList = () => {
    if (listTag) {
      html += "</" + listTag + ">";
      listTag = "";
    }
  };

  lines.forEach((line) => {
    const trimmed = line.trim();
    const isBullet = BULLET.test(trimmed);
    const isNumbered = NUMBERED.test(trimmed);

    if (isBullet || isNumbered) {
      const tag = isBullet ? "ul" : "ol";
      if (listTag !== tag) {
        closeList();
        html += "<" + tag + ">";
        listTag = tag;
      }
      const itemText = trimmed.replace(isBullet ? BULLET : NUMBERED, "");
      if (itemText.length > 0) {
        html += "<li>" + itemText + "</li>";
      }
      return;
    }

    // A blank line between items is spacing, not the end of the list — models
    // routinely separate numbered items that way. Only real text closes it.
    if (trimmed.length === 0) {
      return;
    }

    closeList();
    html += "<p>" + trimmed + "</p>";
  });

  closeList();

  return html;
}

// Citations are pre-numbered + de-duped server-side (footnote N = index+1).
// Keep order; just normalize the link + label per entry.
function normalizeCitations(citations) {
  if (!Array.isArray(citations)) return [];
  return citations.map((c, i) => ({
    url: (c && (c.page_url || c.pdf_url)) || "",
    title: (c && (c.title || c.filename)) || "Source " + (i + 1),
  }));
}

function escapeAttr(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function escapeHtml(s) {
  return String(s)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

// Everything we hold a trustworthy URL for: the cited sources, plus the items
// the server put in this request's prompt. The latter is what lets an answer
// that names several pages end up with all of them linked, even when the model
// only wrote a markdown link for some.
function linkTargets(sources, links) {
  const targets = sources.slice();
  const seen = {};
  targets.forEach((t) => {
    if (t.url) seen[t.url] = true;
  });

  (Array.isArray(links) ? links : []).forEach((l) => {
    const url = (l && l.url) || "";
    const title = (l && l.title) || "";
    if (!url || !title || seen[url]) return;
    seen[url] = true;
    targets.push({ url, title });
  });

  return targets;
}

// Fold the punctuation that differs between a WordPress title and the model's
// retyping of it: curly vs straight quotes, en/em dashes, odd whitespace. Every
// substitution is one character for one character, so offsets stay valid
// against the original string.
function canonical(s) {
  return String(s)
    .toLowerCase()
    .replace(/[‘’‚‛′]/g, "'")
    .replace(/[“”„‟″]/g, '"')
    .replace(/[‐‑‒–—―−]/g, "-")
    .replace(/\s/g, " ");
}

// Index of a whole-word occurrence, so "Blog" doesn't match inside "Bloggers".
function indexOfWhole(haystack, needle) {
  const isWord = (c) => /[a-z0-9]/i.test(c);
  let from = 0;
  for (;;) {
    const idx = haystack.indexOf(needle, from);
    if (idx === -1) return -1;
    const before = idx === 0 ? "" : haystack.charAt(idx - 1);
    const after = haystack.charAt(idx + needle.length);
    if (!isWord(before) && !isWord(after)) return idx;
    from = idx + 1;
  }
}

// Wrap the first verbatim mention of each known title in a [[link:i]] token
// (expanded to a link later). Longest titles first so nested names match
// correctly; URLs come from our own index, so no fabricated links. Titles the
// model already linked itself are gone by now — extractLinks() replaced them
// with tokens — so nothing gets linked twice.
function linkifyTitles(text, targets) {
  let out = typeof text === "string" ? text : "";
  const order = targets
    .map((s, i) => ({ i, s }))
    .filter((x) => x.s.url && x.s.title && x.s.title.length >= 5)
    .sort((a, b) => b.s.title.length - a.s.title.length);

  order.forEach(({ i, s }) => {
    const idx = indexOfWhole(canonical(out), canonical(s.title));
    if (idx === -1) return;
    out = out.slice(0, idx) + "[[link:" + i + "]]" + out.slice(idx + s.title.length);
  });
  return out;
}

// Model output is not trusted enough to emit arbitrary URI schemes: only
// http(s) and site-relative paths may become links.
function safeUrl(url) {
  const u = String(url || "")
    .trim()
    .replace(/[.,;:!?]+$/, "");
  if (/^https?:\/\/\S+$/i.test(u)) return u;
  if (/^\/[^/\s]\S*$/.test(u)) return u;
  return "";
}

// Pull markdown links and bare URLs out into [[a:N]] tokens before anything
// else runs, so the line formatter and the title linkifier can't mangle them
// (and can't nest an anchor inside an anchor).
function extractLinks(text) {
  const links = [];
  let out = typeof text === "string" ? text : "";

  // The URL part tolerates one level of nested parens, so a link whose target
  // contains them is consumed whole rather than leaving a stray ")" behind.
  out = out.replace(
    /\[([^\]\n]+)\]\(([^()\s]*(?:\([^()\s]*\)[^()\s]*)*)\)/g,
    (m, label, url) => {
      const href = safeUrl(url);
      if (!href) return label;
      links.push({ href, label });
      return "[[a:" + (links.length - 1) + "]]";
    }
  );

  out = out.replace(/(^|[\s(])(https?:\/\/[^\s<>()[\]"']+)/gi, (m, lead, url) => {
    // Sentence punctuation after a bare URL belongs to the sentence, not the link.
    const trailing = (url.match(/[.,;:!?]+$/) || [""])[0];
    const bare = trailing ? url.slice(0, -trailing.length) : url;
    const href = safeUrl(bare);
    if (!href) return m;
    links.push({
      href,
      label: href.replace(/^https?:\/\//i, "").replace(/\/$/, ""),
    });
    return lead + "[[a:" + (links.length - 1) + "]]" + trailing;
  });

  return { text: out, links };
}

// Render answer HTML: explicit links, inline page links + footnote superscripts.
function renderAnswerHtml(text, sources, links) {
  const targets = linkTargets(sources, links);
  const extracted = extractLinks(text);
  const html = formatChatText(linkifyTitles(extracted.text, targets));
  if (typeof html !== "string") return html;

  return html
    .replace(/\[\[a:(\d+)\]\]/g, (m, iStr) => {
      const link = extracted.links[parseInt(iStr, 10)];
      if (!link) return "";
      return (
        '<a href="' +
        escapeAttr(link.href) +
        '" target="_blank" rel="noopener noreferrer">' +
        escapeHtml(link.label) +
        "</a>"
      );
    })
    .replace(/\[\[cite:(\d+)\]\]/g, (m, nStr) => {
      const n = parseInt(nStr, 10);
      const src = sources[n - 1];
      if (src && src.url) {
        return (
          '<sup class="chat-cite"><a href="' +
          escapeAttr(src.url) +
          '" target="_blank" rel="noopener noreferrer">' +
          n +
          "</a></sup>"
        );
      }
      return '<sup class="chat-cite">' + n + "</sup>";
    })
    .replace(/\[\[link:(\d+)\]\]/g, (m, iStr) => {
      const src = targets[parseInt(iStr, 10)];
      if (src && src.url) {
        return (
          '<a href="' +
          escapeAttr(src.url) +
          '" target="_blank" rel="noopener noreferrer">' +
          escapeHtml(src.title) +
          "</a>"
        );
      }
      return src ? escapeHtml(src.title) : "";
    });
}

function DialogBubble({ from, text, citations, links }) {
  // Flat disclaimer note — rendered plainly, not as a chat bubble.
  if (from === "disclaimer") {
    return <li className="root-li disclaimer-note">{text}</li>;
  }

  const sources = normalizeCitations(citations);
  const answerHtml = renderAnswerHtml(text, sources, links);

  return (
    <li className={"root-li from-" + from}>
      {(from === "chatbot" || from === "thinking") && (
        <div>
          <Avatar size={24} />
        </div>
      )}
      {from === "thinking" ? (
        <div className="text-wrapper">
          <svg height="20" width="40">
            <circle className="dot" cx="10" cy="10" r="3" />
            <circle className="dot" cx="20" cy="10" r="3" />
            <circle className="dot" cx="30" cy="10" r="3" />
          </svg>
        </div>
      ) : (
        <div className="text-wrapper">
          <div dangerouslySetInnerHTML={{ __html: answerHtml }}></div>

          {sources.length > 0 && (
            <div className="chat-sources">
              <span className="chat-sources-label">Sources:</span>
              <ol>
                {sources.map((s, i) => (
                  <li key={i} value={i + 1}>
                    {s.url ? (
                      <a href={s.url} target="_blank" rel="noopener noreferrer">
                        {s.title}
                      </a>
                    ) : (
                      s.title
                    )}
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}
    </li>
  );
}
